import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { linkify } from "@/components/ExpandableText";
import LinkPreviewCard from "@/components/LinkPreviewCard";
import useLinkPreview from "@/hooks/useLinkPreview";
import AuthorBadges from "@/components/AuthorBadges";
import { toast } from "sonner";
import { MessageCircle, Send, ImagePlus, X, MoreVertical, Pencil, Trash2, CornerDownRight } from "lucide-react";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import MentionPicker from "@/components/MentionPicker";
import { useAuth } from "@/contexts/AuthContext";
import { compressImageIfNeeded } from "@/lib/imageCompress";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const REACTION_EMOJIS = {
  like: "👍", love: "❤️", haha: "😂", wow: "😮", sad: "😢", angry: "😡", care: "🤗",
};


function CommentReactions({ postId, comment, onLocalUpdate }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const meId = useAuth().user?.user_id;
  const reactions = comment.reactions || {};
  const total = Object.entries(reactions).reduce(
    (s, [k, v]) => (k === "by_user" ? s : s + (v || 0)), 0);
  const my = reactions.by_user?.[meId] || null;
  const send = async (emoji) => {
    if (busy) return;
    setBusy(true); setOpen(false);
    try {
      const r = await axios.post(`${API}/posts/${postId}/comments/${comment.comment_id}/react`, { emoji });
      onLocalUpdate({
        ...comment,
        reactions: {
          ...r.data.reactions,
          by_user: { ...(comment.reactions?.by_user || {}), [meId]: r.data.my_reaction || undefined },
        },
      });
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao reagir"); }
    setBusy(false);
  };
  const topEmojis = Object.entries(reactions).filter(([k, v]) => k !== "by_user" && v > 0).sort((a, b) => b[1] - a[1]).slice(0, 3).map(([k]) => REACTION_EMOJIS[k]).join("");
  return (
    <div className="relative inline-flex items-center gap-1">
      <button
        data-testid={`comment-react-btn-${comment.comment_id}`}
        onClick={() => send(my ? my : "like")}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        disabled={busy}
        className={`text-[11px] font-semibold hover:underline transition-colors ${my ? "text-primary" : "text-muted-foreground"}`}
      >
        {my ? <><span className="mr-1">{REACTION_EMOJIS[my]}</span>Curtido</> : "Curtir"}
      </button>
      {total > 0 && (
        <span data-testid={`comment-like-count-${comment.comment_id}`} title={`${total} ${total === 1 ? "curtida" : "curtidas"}`}
          className="inline-flex items-center gap-0.5 rounded-full bg-background border border-border px-1.5 py-px text-[10px] font-semibold text-foreground shadow-sm animate-pop-in">
          <span className="text-[11px] leading-none">{topEmojis || "👍"}</span>{total}
        </span>
      )}
      {open && (
        <div
          onMouseEnter={() => setOpen(true)}
          onMouseLeave={() => setOpen(false)}
          className="absolute bottom-full left-0 mb-1 bg-popover border rounded-full px-2 py-1 flex gap-1 shadow-lg z-30"
        >
          {Object.entries(REACTION_EMOJIS).map(([k, e]) => (
            <button key={k} onClick={() => send(k)} className="text-xl hover:scale-125 transition-transform" title={k}>{e}</button>
          ))}
        </div>
      )}
    </div>
  );
}

function CommentInput({ postId, parentId, onDone, initial, editingId, testId, replyToName, autoFocus }) {
  const [value, setValue] = useState(initial?.content || "");
  const [imageUrl, setImageUrl] = useState(initial?.image_url || "");
  const [busy, setBusy] = useState(false);
  const linkPrev = useLinkPreview(value);
  const [picked, setPicked] = useState(initial?.mentions || []);
  const [uploading, setUploading] = useState(false);
  const ref = useRef(null);
  const tid = testId || editingId || parentId || "root";
  useEffect(() => { if (autoFocus) ref.current?.focus(); }, [autoFocus]);

  const uploadImage = async (e) => {
    let file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      file = await compressImageIfNeeded(file);
      const fd = new FormData(); fd.append("file", file);
      const r = await axios.post(`${API}/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" }, timeout: 60000,
      });
      setImageUrl(r.data.url);
    } catch { toast.error("Erro no upload"); }
    setUploading(false);
    e.target.value = "";
  };

  const submit = async () => {
    const text = value.trim();
    if ((!text && !imageUrl) || busy) return;
    setBusy(true);
    try {
      let res;
      const lp = { link_preview: linkPrev.preview || null, skip_link_preview: linkPrev.dismissed,
        mention_ids: picked.filter((u) => text.toLowerCase().includes(`@${(u.name || "").split(" ")[0].toLowerCase()}`)).map((u) => u.user_id) };
      if (editingId) {
        res = await axios.patch(`${API}/posts/${postId}/comments/${editingId}`, {
          content: text, image_url: imageUrl || null, ...lp,
        });
      } else {
        res = await axios.post(`${API}/posts/${postId}/comment`, {
          content: text, image_url: imageUrl || null, parent_comment_id: parentId || undefined, ...lp,
        });
      }
      onDone(res.data);
      linkPrev.reset();
      if (!editingId) { setValue(""); setImageUrl(""); setPicked([]); }
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
    setBusy(false);
  };

  return (
    <div className="relative mt-2">
      {(linkPrev.preview || linkPrev.loading) && (
        <div className="mb-2 max-w-sm">
          <LinkPreviewCard preview={linkPrev.preview} loading={linkPrev.loading} onRemove={linkPrev.dismiss} testid={`comment-link-preview-${tid}`} />
        </div>
      )}
      {imageUrl && (
        <div className="relative mb-2 inline-block">
          <img src={imageUrl} alt="preview" className="max-h-40 rounded-lg" />
          <button
            onClick={() => setImageUrl("")}
            className="absolute -top-2 -right-2 bg-black/70 text-white rounded-full p-1"
            data-testid={`comment-image-remove-${parentId || "root"}`}
          ><X className="w-3 h-3" /></button>
        </div>
      )}
      <div className="flex gap-2">
        <Input
          ref={ref}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && submit()}
          placeholder={editingId ? "Editar comentário..." : parentId ? `Respondendo a ${replyToName || "comentário"}...` : "Escreva um comentário... (use @ para mencionar)"}
          disabled={busy}
          className="text-sm"
          data-testid={`comment-input-${tid}`}
        />
        <label className="inline-flex items-center cursor-pointer px-2 rounded-md hover:bg-accent flex-shrink-0">
          <ImagePlus className="w-4 h-4" />
          <input type="file" accept="image/*" className="hidden" onChange={uploadImage}
            data-testid={`comment-image-btn-${tid}`} disabled={uploading} />
        </label>
        <Button onClick={submit} disabled={busy || uploading || (!value.trim() && !imageUrl)} size="icon" className="rounded-full flex-shrink-0"
          data-testid={`comment-submit-${tid}`}>
          <Send className="w-4 h-4" />
        </Button>
      </div>
      <MentionPicker value={value} onChange={setValue} inputRef={ref} onPick={(u) => setPicked((p) => (p.some((x) => x.user_id === u.user_id) ? p : [...p, u]))} />
    </div>
  );
}

function CommentBody({ postId, c, onLocalUpdate, onDeleted, onEdited, showReplyButton, onReply }) {
  const [editing, setEditing] = useState(false);
  const meId = useAuth().user?.user_id;
  const isMine = c.user_id === meId;

  if (editing) {
    return (
      <div className="flex-1 min-w-0">
        <CommentInput
          postId={postId}
          editingId={c.comment_id}
          initial={c}
          onDone={(updated) => { onEdited(updated); setEditing(false); }}
        />
        <button onClick={() => setEditing(false)} className="text-[11px] text-muted-foreground hover:underline mt-1">Cancelar</button>
      </div>
    );
  }
  return (
    <div className="flex-1 min-w-0">
      <div className="bg-muted rounded-2xl px-3 py-2 relative group">
        <div className="flex items-start justify-between">
          <Link to={`/user/${c.user_id}`} className="text-xs font-semibold hover:underline" data-testid={`comment-author-link-${c.comment_id}`}>{c.author_name}</Link>
          <AuthorBadges badges={c.author_badges} size="xs" testid={`comment-author-badges-${c.comment_id}`} />
          {isMine && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="opacity-40 hover:opacity-100 transition-opacity" data-testid={`comment-menu-${c.comment_id}`}>
                  <MoreVertical className="w-3.5 h-3.5" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-32">
                <DropdownMenuItem onClick={() => setEditing(true)} data-testid={`comment-edit-${c.comment_id}`}>
                  <Pencil className="w-3.5 h-3.5 mr-2" /> Editar
                </DropdownMenuItem>
                <DropdownMenuItem
                  className="text-red-500 focus:text-red-600"
                  onClick={async () => {
                    if (!window.confirm("Excluir este comentário?")) return;
                    try {
                      const r = await axios.delete(`${API}/posts/${postId}/comments/${c.comment_id}`);
                      onDeleted(c.comment_id, r.data?.removed || 1);
                    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
                  }}
                  data-testid={`comment-delete-${c.comment_id}`}
                >
                  <Trash2 className="w-3.5 h-3.5 mr-2" /> Excluir
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
        {c.reply_to?.name && c.reply_to.comment_id !== c.parent_comment_id && (
          <div className="text-[10px] text-muted-foreground flex items-center gap-1 -mt-0.5 mb-0.5" data-testid={`comment-replying-to-${c.comment_id}`}>
            <CornerDownRight className="w-3 h-3" /> Respondendo a <Link to={`/user/${c.reply_to.user_id}`} className="font-semibold hover:underline">{c.reply_to.name}</Link>
          </div>
        )}
        {c.content && <div className="text-sm whitespace-pre-wrap break-words">{linkify(c.content, { mentions: c.mentions })}</div>}
        {c.link_preview && <div className="mt-2"><LinkPreviewCard preview={c.link_preview} compact testid={`comment-link-${c.comment_id}`} /></div>}
        {c.image_url && <img src={c.image_url} alt="" className="mt-2 max-h-48 rounded-lg" />}
        {c.video_url && <video src={c.video_url} controls className="mt-2 max-h-48 rounded-lg" />}
      </div>
      <div className="flex items-center gap-3 mt-1 px-2">
        <CommentReactions postId={postId} comment={c} onLocalUpdate={onLocalUpdate} />
        {showReplyButton && (
          <button onClick={onReply} data-testid={`reply-toggle-${c.comment_id}`}
            className="text-[11px] font-semibold text-muted-foreground hover:underline">Responder</button>
        )}
        <span className="text-[10px] text-muted-foreground opacity-70">
          {c.created_at ? new Date(c.created_at).toLocaleString() : ""}
          {c.edited_at && <span className="italic ml-1 opacity-70">(editado)</span>}
        </span>
      </div>
    </div>
  );
}

function CommentBlock({ postId, root, replies, onLocalUpdate, onNewReply, onDeleted, onEdited }) {
  const [replying, setReplying] = useState(null); // {parentId, name}
  const [showReplies, setShowReplies] = useState(true);
  const toggleReply = (c) => setReplying((cur) => (cur?.parentId === c.comment_id ? null : { parentId: c.comment_id, name: c.author_name }));
  return (
    <div data-testid={`comment-${root.comment_id}`}>
      <div className="flex items-start gap-2">
        <Link to={`/user/${root.user_id}`} className="flex-shrink-0 hover:opacity-80" data-testid={`comment-avatar-link-${root.comment_id}`}>
          <Avatar className="w-8 h-8">
            <AvatarImage src={root.author_picture} />
            <AvatarFallback>{root.author_name?.[0]}</AvatarFallback>
          </Avatar>
        </Link>
        <CommentBody
          postId={postId} c={root}
          onLocalUpdate={onLocalUpdate} onDeleted={onDeleted} onEdited={onEdited}
          showReplyButton onReply={() => toggleReply(root)}
        />
      </div>
      {replies && replies.length > 0 && (
        <div className="mt-2 space-y-2 pl-4 border-l-2 border-muted ml-4">
          {!showReplies ? (
            <button onClick={() => setShowReplies(true)}
              className="text-[11px] font-semibold text-muted-foreground hover:underline flex items-center gap-1">
              <MessageCircle className="w-3 h-3" /> Ver {replies.length} {replies.length === 1 ? "resposta" : "respostas"}
            </button>
          ) : (
            replies.map((r) => (
              <div key={r.comment_id} className="flex items-start gap-2" data-testid={`comment-${r.comment_id}`}>
                <Link to={`/user/${r.user_id}`} className="flex-shrink-0 hover:opacity-80" data-testid={`comment-avatar-link-${r.comment_id}`}>
                  <Avatar className="w-7 h-7">
                    <AvatarImage src={r.author_picture} />
                    <AvatarFallback>{r.author_name?.[0]}</AvatarFallback>
                  </Avatar>
                </Link>
                <CommentBody
                  postId={postId} c={r}
                  onLocalUpdate={onLocalUpdate} onDeleted={onDeleted} onEdited={onEdited}
                  showReplyButton onReply={() => toggleReply(r)}
                />
              </div>
            ))
          )}
        </div>
      )}
      {replying && (
        <div className="pl-10" data-testid={`reply-box-${root.comment_id}`}>
          <div className="text-[10px] text-muted-foreground flex items-center gap-1 mt-1">
            <CornerDownRight className="w-3 h-3" /> Respondendo a <b>{replying.name}</b>
            <button onClick={() => setReplying(null)} className="ml-1 hover:underline" data-testid={`reply-cancel-${root.comment_id}`}>cancelar</button>
          </div>
          <CommentInput
            postId={postId} parentId={replying.parentId} replyToName={replying.name} autoFocus
            onDone={(newC) => { onNewReply(newC); setReplying(null); }}
          />
        </div>
      )}
    </div>
  );
}

export default function CommentThread({ postId, comments, onNewComment, onLocalUpdate, onDeleted, onEdited, inputTestId, hideInput = false, onLinkClick }) {
  const grouped = useMemo(() => {
    const roots = [];
    const map = {};
    for (const c of comments || []) {
      if (c.parent_comment_id) (map[c.parent_comment_id] = map[c.parent_comment_id] || []).push(c);
      else roots.push(c);
    }
    return roots.map((r) => ({ root: r, replies: map[r.comment_id] || [] }));
  }, [comments]);

  return (
    <div className="space-y-3" data-testid={`comment-thread-${postId}`}>
      {grouped.length === 0 && <div className="text-xs text-muted-foreground italic">Seja o primeiro a comentar</div>}
      {grouped.map(({ root, replies }) => (
        <CommentBlock
          key={root.comment_id}
          postId={postId} root={root} replies={replies}
          onLocalUpdate={onLocalUpdate} onNewReply={onNewComment}
          onDeleted={onDeleted} onEdited={onEdited}
        />
      ))}
      {!hideInput && <CommentInput postId={postId} onDone={onNewComment} testId={inputTestId} />}
    </div>
  );
}
